import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import {
  createCommentRequest,
  createCommentReplyRequest,
  loadCommentReplies,
  loadCommentSummary,
  updateCommentRequest,
  useLoadTouchpointComments,
} from "hooks/api/use-touchpoint-comments";

import { Comment, NewComment } from "types/comments";
import { PaginatedRequestOptions } from "types/pagination";

function toPageable(options?: PaginatedRequestOptions, fallbackSort?: string) {
  const sort = options?.sort ?? fallbackSort;
  return {
    page: options?.page,
    size: options?.size,
    sort: sort ? [sort] : undefined,
  };
}

export const useTouchpointOperations = (selectedVersionId?: string, isClientUser?: boolean) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  const scope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };

  const fetchTouchpointComments = useLoadTouchpointComments(scope);

  const loadTouchpointComments = (options?: PaginatedRequestOptions) =>
    fetchTouchpointComments({
      filter: options?.filter,
      pageable: toPageable(options, "comment.createdDate,desc"),
    });

  const loadCommentsSummary = () => loadCommentSummary(scope);

  const submitNewTouchpointComment = (comment: NewComment) => {
    if (!selectedVersionId) {
      return Promise.reject(new Error("Cannot comment without a selected version"));
    }
    const payload = {
      text: comment.text,
      actionItem: comment.actionItem,
      annotationId: comment.annotationId,
      hidden: isClientUser ? false : comment.hidden,
      versionId: selectedVersionId,
    };
    return createCommentRequest(scope, payload);
  };

  const updateTouchpointComment = (comment: Comment) =>
    updateCommentRequest(scope, comment.id, {
      hidden: comment.hidden,
      actionItem: comment.actionItem,
      open: comment.open,
    });

  const loadTouchpointCommentReplies = (comment: Comment) =>
    loadCommentReplies(scope, comment.id, { sort: ["createdDate,asc"] });

  const submitNewTouchpointCommentReply = (newComment: NewComment) => {
    if (!newComment.parentId) {
      return Promise.reject(new Error("Cannot reply to a comment without parentId"));
    }
    return createCommentReplyRequest(scope, newComment.parentId, {
      text: newComment.text,
    });
  };

  return {
    loadTouchpointComments,
    loadCommentsSummary,
    submitNewTouchpointComment,
    updateTouchpointComment,
    loadTouchpointCommentReplies,
    submitNewTouchpointCommentReply,
  };
};
